import React, { ReactNode, useMemo, useState } from 'react';

import { CheckCircle, Error, AddCircleOutline } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Typography,
  Divider,
  Grid,
  Chip,
  Box,
  Autocomplete,
  TextField,
  Stack,
} from '@mui/material';

import {
  addCandidateToOrganization,
  addCandidateToTranche,
  batchCreateFlags,
  fetchCandidate,
  removeCandidateFromTranche,
} from 'api';
import { AddFlag } from 'components/AddFlag';
import { InfoCell } from 'components/InfoCell';
import { getErrorDetail } from 'helpers';
import { convertToDateShort } from 'helpers/formatHelper';
import { useCandidateAdminContext } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import { useUserContext } from 'hooks/useUserContext';
import {
  ReviewedFullCandidateProps,
  TrancheProps,
  Option,
  FlagType,
  Maybe,
} from 'types';
import { FullValidatedWorkClaimProps } from 'types/Claims';

import { Reference } from '../../candidate/Reference';
import { InfoCellColumn, InfoCellRow } from '../../candidate/styledComponents';
import { CivStatusCode, CIV_STATUS_BY_CODE } from './civStatus';
import { FlagCard } from './FlagCard';
import { ReviewedCandidateSummaryTable } from './ReviewedCandidateSummaryTable';
import { HeaderWrapper } from './styledComponents';
import { ValidatedWorkPeriodInfo } from './ValidatedWorkPeriodInfo';

export interface ReviewedCandidateFormProps {
  candidate: ReviewedFullCandidateProps;
  setCandidate: React.Dispatch<
    React.SetStateAction<ReviewedFullCandidateProps>
  >;
  validatedWorkClaims: Array<FullValidatedWorkClaimProps>;
}

export const ReviewedCandidate = (props: ReviewedCandidateFormProps) => {
  const { candidate, setCandidate, validatedWorkClaims } = props;
  const {
    firstname,
    lastname,
    birthdate,
    validated_work_claims,
    passport_number,
    tazkera,
    work_claims,
    validated_work_days,
    flags,
    tranche,
    applications,
    id,
    is_reviewable,
    siv_status,
    case_number,
  } = candidate;
  const { user } = useUserContext();
  const { setSnack } = useSnackContext();
  const { organizationOptionArray } = useCandidateAdminContext();
  const [addFlagModal, setAddFlagModal] = useState(false);
  const [addingEmployer, setAddingEmployer] = useState(false);
  const [orgOptionToAdd, setOrgOptionToAdd] = useState<Option | null>(null);
  const [removingCandidateFromTranche, setRemovingCandidateFromTranche] =
    useState(false);

  const handleAddFlags = async (
    candidateIds: Array<number>,
    flagType: FlagType,
    message: string,
  ) => {
    const { data } = await batchCreateFlags(candidateIds, {
      type: flagType,
      message: message,
    });
    data?.forEach((candidate) => {
      setCandidate(candidate);
    });
  };

  const handleRemoveCandidateFromTranche = async () => {
    try {
      setRemovingCandidateFromTranche(true);
      const { data } = await removeCandidateFromTranche(id);
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'Removed Candidate From Tranche',
      }));
      setCandidate(data);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Remove Candidate From Tranche',
        }),
        error: e as Error,
      }));
      setRemovingCandidateFromTranche(false);
    } finally {
      setRemovingCandidateFromTranche(false);
    }
  };

  const displayTrancheCard = (tranche: TrancheProps) => {
    switch (tranche.status) {
      case 'sent_to_state':
        return (
          <Chip
            color='success'
            label={`Sent in Tranche ${tranche.sequence_no}`}
          />
        );
      case 'signature_pending':
        return (
          <Chip
            color='warning'
            label={`Awaiting Signature in Tranche ${tranche.sequence_no}`}
          />
        );

      case 'active':
        return (
          <Stack>
            <Chip
              color='secondary'
              label={`In Active Tranche ${tranche.sequence_no}`}
            />

            <LoadingButton
              disabled={!is_reviewable}
              loading={removingCandidateFromTranche}
              onClick={handleRemoveCandidateFromTranche}
            >
              REMOVE FROM TRANCHE
            </LoadingButton>
          </Stack>
        );

      default:
        return <Chip label={`In Tranche ${tranche.sequence_no}`} />;
    }
  };

  const derogStatus = useMemo(() => {
    return validatedWorkClaims
      .map((validated_work_claim) => validated_work_claim.has_derog)
      .includes(true);
  }, [validatedWorkClaims]);
  const [trancheLoading, setTrancheLoading] = useState(false);
  const tazkeras = tazkera?.split(',');
  const passports = passport_number?.split(',');
  const orgIds = useMemo(() => {
    return applications.map((app) => app.organization_id);
  }, [applications]);
  const addOrganization = async (orgId: number, candidateId: number) => {
    try {
      await addCandidateToOrganization({
        candidateId: candidateId,
        orgId: orgId,
      });
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'Added Organization To Candidate',
      }));
      const res = await fetchCandidate(id);
      setCandidate((prevCandidate) => ({
        ...prevCandidate,
        applications: res.data.applications,
      }));
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Add Candidate To Organization',
        }),
        error: e as Error,
      }));
    } finally {
      setAddingEmployer(false);
      setOrgOptionToAdd(null);
    }
  };

  const hasValidWorkClaims = validated_work_claims.length > 0;
  const hasMoreThan365WorkDays = validated_work_days >= 365;
  const headerSummaryData = useMemo(() => {
    const headerData: Array<{
      title: string;
      icon: Maybe<'success' | 'error'>;
      value: string | ReactNode;
    }> = [];

    headerData.push({
      title: '365 Day Status',
      icon: hasValidWorkClaims
        ? hasMoreThan365WorkDays
          ? 'success'
          : 'error'
        : null,
      value: hasValidWorkClaims
        ? hasMoreThan365WorkDays
          ? 'Confirmed'
          : 'Less than 365'
        : 'Unreviewed',
    });

    headerData.push({
      title: 'Derog Status',
      icon: hasValidWorkClaims ? (derogStatus ? 'error' : 'success') : null,
      value: hasValidWorkClaims
        ? derogStatus
          ? 'Derog Reported'
          : 'None Reported'
        : 'Unreviewed',
    });

    const civStatusData =
      siv_status in CIV_STATUS_BY_CODE
        ? CIV_STATUS_BY_CODE[siv_status as CivStatusCode]
        : null;

    headerData.push({
      title: 'State Dept Status Code',
      icon: civStatusData
        ? civStatusData.actionType === 'review'
          ? 'success'
          : civStatusData.actionType === 'no'
          ? 'error'
          : null
        : null,
      value: (
        <Typography variant='body1' component='span'>
          <Typography component='span' variant='body1' fontWeight='bold'>
            {siv_status}
            {civStatusData ? ` / ${civStatusData.description}: ` : ''}
          </Typography>
          {civStatusData ? civStatusData.actionText : null}
        </Typography>
      ),
    });
    return headerData;
  }, [hasValidWorkClaims, hasMoreThan365WorkDays, derogStatus, siv_status]);

  return (
    <div aria-label={'Review page for candidate ' + firstname + ' ' + lastname}>
      <HeaderWrapper aria-label={'Candidate ' + firstname + ' ' + lastname}>
        <Typography variant='h4'>{`${firstname} ${lastname}`}</Typography>
        {tranche ? (
          displayTrancheCard(tranche)
        ) : (
          <LoadingButton
            loading={trancheLoading}
            variant='outlined'
            disabled={!(validated_work_days > 0) || !is_reviewable}
            onClick={async () => {
              setTrancheLoading(true);
              try {
                const { data } = await addCandidateToTranche(id);
                setCandidate(data);
              } catch (e) {
                setSnack((props) => ({
                  ...props,
                  severity: 'error',
                  open: true,
                  message: getErrorDetail({
                    error: e,
                    fallback: 'Failed To Add Candidate To Tranche',
                  }),
                  error: e as Error,
                }));
              } finally {
                setTrancheLoading(false);
              }
            }}
          >
            Add To Tranche
          </LoadingButton>
        )}
      </HeaderWrapper>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography variant='h5'>Summary of Verified Data</Typography>
      <Grid container direction='row' justifyContent='space-between'>
        {headerSummaryData.map(({ title, icon, value }) => (
          <Grid item maxWidth='30%' key={title}>
            <Typography variant='overline' sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
            <div>
              {icon ? (
                icon === 'success' ? (
                  <CheckCircle
                    color='success'
                    fontSize='small'
                    display='inline'
                    style={{ verticalAlign: 'text-bottom' }}
                  />
                ) : (
                  <Error
                    color='error'
                    fontSize='small'
                    display='inline'
                    style={{ verticalAlign: 'text-bottom' }}
                  />
                )
              ) : null}
              {typeof value === 'string' ? (
                <Typography variant='body1' component='span'>
                  {value}
                </Typography>
              ) : (
                value
              )}
            </div>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography sx={{ mb: 2 }} variant='h5'>
        Details
      </Typography>
      <ReviewedCandidateSummaryTable
        applications={applications}
        validated_work_claims={validatedWorkClaims}
        trancheStatus={tranche?.status}
        setCandidate={setCandidate}
      />
      {user && user.role === 'candidate_admin' && (
        <Button
          sx={{ mt: 1 }}
          disabled={!is_reviewable}
          variant='contained'
          onClick={() => {
            if (addingEmployer) {
              setOrgOptionToAdd(null);
            }
            setAddingEmployer(!addingEmployer);
          }}
        >
          {addingEmployer ? 'Stop Editing' : 'Add Employer'}
        </Button>
      )}

      {orgOptionToAdd && (
        <LoadingButton
          disabled={!is_reviewable}
          sx={{ ml: 1 }}
          onClick={async () =>
            addOrganization(orgOptionToAdd.value as number, id)
          }
        >
          {`Confirm add ${orgOptionToAdd.label}`}
        </LoadingButton>
      )}
      {addingEmployer && (
        <Autocomplete
          sx={{ mt: 1 }}
          fullWidth
          size='small'
          filterSelectedOptions
          getOptionLabel={(option) => option.label || 'nolabel'}
          renderInput={(params) => {
            return <TextField {...params} label='Employer Name' />;
          }}
          onChange={(_, option) => {
            setOrgOptionToAdd(option);
          }}
          options={organizationOptionArray
            .filter((option) => !orgIds.includes(option.value as number))
            .sort((a, b) => a.label?.localeCompare(b.label))}
        />
      )}

      <Divider sx={{ mt: 4, mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='h5'>Flags</Typography>
        <Button
          sx={{ ml: 2 }}
          disabled={!is_reviewable}
          variant='outlined'
          onClick={() => setAddFlagModal(true)}
          startIcon={<AddCircleOutline />}
        >
          ADD FLAG
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexFlow: 'row', mt: 2 }}>
        {flags.map((flag) => {
          return (
            <FlagCard key={flag.id} flag={flag} setCandidate={setCandidate} />
          );
        })}
      </Box>

      <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography variant='h5'>Reported Biographical Information</Typography>

      <>
        <InfoCellRow>
          <InfoCellColumn>
            {InfoCell({
              title: 'Date Of Birth (DD/MM/YYYY)',
              value: convertToDateShort(birthdate),
            })}
          </InfoCellColumn>
        </InfoCellRow>

        {(tazkeras && tazkeras?.length > 1) || passports?.length > 1 ? (
          <>
            {tazkeras
              ?.reduce(
                (
                  previousValue: Array<Array<string>>,
                  currentValue: string,
                  currentIndex: number,
                  array: Array<string>,
                ) => {
                  if (currentIndex % 2 === 0) {
                    previousValue.push(
                      array.slice(currentIndex, currentIndex + 2),
                    );
                  }
                  return previousValue;
                },
                [],
              )
              .map((cells, index) => {
                return (
                  <InfoCellRow key={`tazkeraInfoRow${cells[0]}`}>
                    {cells[0] && (
                      <InfoCellColumn>
                        {InfoCell({
                          title:
                            index === 0
                              ? 'Tazkera Number'
                              : 'Alternative Tazkera Number',
                          value: cells[0],
                        })}
                      </InfoCellColumn>
                    )}
                    {cells[1] && (
                      <InfoCellColumn>
                        {InfoCell({
                          title: 'Alternative Tazkera Number',
                          value: cells[1],
                        })}
                      </InfoCellColumn>
                    )}
                  </InfoCellRow>
                );
              })}
            {passports
              ?.reduce(
                (
                  previousValue: Array<Array<string>>,
                  currentValue: string,
                  currentIndex: number,
                  array: Array<string>,
                ) => {
                  if (currentIndex % 2 === 0) {
                    previousValue.push(
                      array.slice(currentIndex, currentIndex + 2),
                    );
                  }
                  return previousValue;
                },
                [],
              )
              .map((cells, index) => {
                return (
                  <InfoCellRow key={`passportInfoRow${cells[0]}`}>
                    {cells[0] && (
                      <InfoCellColumn>
                        {InfoCell({
                          title:
                            index === 0
                              ? 'Passport Number'
                              : 'Alternative Passport Number',
                          value: cells[0],
                        })}
                      </InfoCellColumn>
                    )}
                    {cells[1] && (
                      <InfoCellColumn>
                        {InfoCell({
                          title: 'Alternative Passport Number',
                          value: cells[1],
                        })}
                      </InfoCellColumn>
                    )}
                  </InfoCellRow>
                );
              })}
            {case_number && (
              <InfoCellRow>
                <InfoCellColumn>
                  {InfoCell({
                    title: 'NVCSIV ID',
                    value: case_number,
                  })}
                </InfoCellColumn>
              </InfoCellRow>
            )}
          </>
        ) : (
          <InfoCellRow>
            {tazkera && (
              <InfoCellColumn>
                {InfoCell({ title: 'Tazkera', value: tazkera })}
              </InfoCellColumn>
            )}
            {passport_number && (
              <InfoCellColumn>
                {InfoCell({
                  title: 'Passport Number',
                  value: passport_number,
                })}
              </InfoCellColumn>
            )}
            {case_number && (
              <InfoCellColumn>
                {InfoCell({
                  title: 'NVCSIV ID',
                  value: case_number,
                })}
              </InfoCellColumn>
            )}
          </InfoCellRow>
        )}
      </>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography variant='h5'>Work Periods Reported by Candidate</Typography>
      {work_claims?.map((work_claim) => (
        <Reference key={work_claim.id} {...work_claim} />
      ))}
      {validatedWorkClaims.length > 0 && (
        <>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant='h5'>Information Reported by Employer</Typography>
          {validatedWorkClaims?.map((validated_work_claim) => {
            return (
              <ValidatedWorkPeriodInfo
                key={validated_work_claim.id}
                {...validated_work_claim}
              />
            );
          })}
        </>
      )}

      <Divider sx={{ mt: 4, mb: 2 }} />
      <AddFlag
        open={addFlagModal}
        candidateIds={[id]}
        handleAddFlags={handleAddFlags}
        handleClose={() => setAddFlagModal(false)}
      />
    </div>
  );
};
